import React, { useCallback, useEffect } from 'react';
import { GenericStep } from '@/components/FullWidthTemplate/GenericStep';
import * as Styles from './PlanTurboFreeDomainStep.styles';
import {
  Checkbox, SelectField, Skeleton, Tag, TextField,
} from 'gatorcomponents';
import DOMPurify from 'dompurify';
import NavBar from '@/media/onboarding/navbar.svg';
import { domainsActions } from '@/redux/modules';
import { useDispatch } from 'react-redux';

const PlanTurboFreeDomainStep = ({
  locales,
  buttonsProps,
  domainSld,
  handleRedeemDomain,
  loadingRedeem,
  radixTldSelected,
  setRadixTldSelected,
  listRadixOptions,
  currentRadix,
  loadingDomainAvailability,
  resetCurrentRadixDomainAvailability,
  refuseRadix,
  setRefuseRadix,
}) => {
  const dispatch = useDispatch();

  const hasRadixError = !loadingRedeem && !loadingDomainAvailability && !refuseRadix && currentRadix.domain && !currentRadix.isAvailable;

  const mountRadixLabel = tld => (
    <Styles.TagLabel>
      {tld}
      <Tag label={locales.freeTag} variant="info" size="sm" />
    </Styles.TagLabel>
  );

  useEffect(() => {
    if (currentRadix.domain && currentRadix.isAvailable && !loadingRedeem) {
      handleRedeemDomain({ isTurbo: true, redeemRadix: !refuseRadix, radixTld: radixTldSelected.value });
    }
  }, [currentRadix.domain, currentRadix.isAvailable, handleRedeemDomain, refuseRadix, radixTldSelected.value, loadingRedeem]);

  const listRadixOptionsFormatted = listRadixOptions.map(tld => ({ value: tld, label: mountRadixLabel(tld) }));

  const genericStepProps = useCallback(() => {
    const rightButtonCta = () => {
      if (!refuseRadix) {
        return dispatch(domainsActions.domainAvailability.request({
          sld: domainSld,
          tld: radixTldSelected.value.slice(1),
          isRadix: true,
        }));
      }
      return handleRedeemDomain({ isTurbo: true, redeemRadix: false, radixTld: null });
    };

    const customButtonsProps = {
      ...buttonsProps,
      rightButton: {
        ...buttonsProps.rightButton,
        onClick: rightButtonCta,
        disabled: (!refuseRadix && radixTldSelected.value === '') || loadingDomainAvailability || loadingRedeem,
      },
    };

    return {
      icon: null,
      title: null,
      description: null,
      imageSrc: null,
      testId: 'plan-turno-free-domain-step',
      buttonsProps: customButtonsProps,
    };
  }, [
    buttonsProps,
    refuseRadix,
    radixTldSelected,
    dispatch,
    domainSld,
    loadingDomainAvailability,
    loadingRedeem,
    handleRedeemDomain,
  ]);

  return (
    <GenericStep {...genericStepProps()} contentWidthFlexible>
      <Styles.Wrapper>
        <Styles.Title>{locales.title}</Styles.Title>
        <Styles.Description dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(locales.description) }} />

        <Styles.NavWrapper>
          <Styles.NavbarContainer>
            <Styles.NavbarImage src={NavBar} />
          </Styles.NavbarContainer>
          <Styles.DomainInputWrapper>
            <Styles.DomainFieldsContainer>
              <Styles.TextFieldContainer>
                {loadingRedeem && (
                  <Skeleton width="100%" height="56px" />
                )}
                {!loadingRedeem && (
                  <TextField
                    testId="input-sld"
                    label=""
                    value={domainSld}
                    disabled
                  />
                )}
              </Styles.TextFieldContainer>
              <Styles.SelectFieldContainer>
                {loadingRedeem && (
                  <Skeleton width="100%" height="56px" />
                )}
                {!loadingRedeem && (
                  <SelectField
                    testId="select-tld"
                    withScroll
                    options={listRadixOptionsFormatted}
                    placeholder={locales.placeholderRadix}
                    value={radixTldSelected.label}
                    onClickSelect={({ label, value }) => {
                      setRadixTldSelected({ label, value });
                      resetCurrentRadixDomainAvailability();
                    }}
                    disabled={loadingRedeem || refuseRadix}
                    clearValue={!radixTldSelected.value}
                    error={hasRadixError}
                  />
                )}
              </Styles.SelectFieldContainer>
            </Styles.DomainFieldsContainer>
          </Styles.DomainInputWrapper>

        </Styles.NavWrapper>
        {hasRadixError && (
        <Styles.ErrorText>
          {locales.radixUnavailable[radixTldSelected.value.slice(1)](currentRadix.domain)}
        </Styles.ErrorText>
        )}

        <Styles.CheckboxLabel
          htmlFor="refuse-radix"
          data-testid="check-agreement-label"
          onClick={() => {
            if (loadingRedeem || loadingDomainAvailability) return;
            setRadixTldSelected({ value: '', label: '' });
            resetCurrentRadixDomainAvailability();
            setRefuseRadix(!refuseRadix);
          }}
        >
          <Checkbox checked={refuseRadix} id="refuse-radix" />
          <Styles.CheckboxDescription>
            {locales.refuseRadix}
          </Styles.CheckboxDescription>
        </Styles.CheckboxLabel>
        <Styles.Disclaimer>
          {locales.disclaimer}
        </Styles.Disclaimer>
      </Styles.Wrapper>
    </GenericStep>
  );
};

export default PlanTurboFreeDomainStep;
